import React, { useState, useEffect } from "react";
import { Label, Badge, Input, Button } from "@/components/ui";
import { BookOpen, X, Search } from "lucide-react";
import financeService from "@/services/financeService";

/**
 * Account Filter Component
 *
 * Multi-select filter for accounts with search functionality.
 * Requirements: 5.1-5.9
 */
const AccountFilter = ({ selectedAccounts = [], onChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await financeService.getAccounts();
      if (response?.data?.success) {
        setAccounts(response.data.data?.accounts || []);
      }
    } catch (error) {
      console.error("Failed to load accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(
    (account) =>
      account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.code?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleToggleAccount = (accountId) => {
    const newSelection = selectedAccounts.includes(accountId)
      ? selectedAccounts.filter((id) => id !== accountId)
      : [...selectedAccounts, accountId];
    onChange(newSelection);
  };

  const handleRemoveAccount = (accountId) => {
    onChange(selectedAccounts.filter((id) => id !== accountId));
  };

  const getAccountName = (accountId) => {
    const account = accounts.find((a) => a.id === accountId);
    return account ? `${account.code} - ${account.name}` : accountId;
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <BookOpen className="h-4 w-4" />
        Accounts
      </Label>

      {/* Selected accounts */}
      {selectedAccounts.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedAccounts.map((accountId) => (
            <Badge
              key={accountId}
              variant="secondary"
              className="flex items-center gap-1"
            >
              {getAccountName(accountId)}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveAccount(accountId)}
              />
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            className="pl-10"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Loading accounts...
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No accounts found
              </div>
            ) : (
              filteredAccounts.map((account) => (
                <div
                  key={account.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 ${
                    selectedAccounts.includes(account.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={() => handleToggleAccount(account.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{account.code}</div>
                      <div className="text-sm text-gray-600">
                        {account.name}
                      </div>
                    </div>
                    {selectedAccounts.includes(account.id) && (
                      <div className="text-blue-600">✓</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default AccountFilter;
